import { AppUpdate } from '@capawesome/capacitor-app-update';
import { useMobileLogout } from '@flexo/hooks';
import { ALERT } from '@flexo/reducers';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styles from './../alert.module.scss';

/**
 * Alert component for app update notification.
 * Shows when a new version is available on the store.
 *
 * - "Update" button: logs out user and opens the app store
 * - "Not now" button: dismisses the alert and continues using the app
 */
export default function AlertAppUpdate() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const alertState = useSelector((state: any) => state.alert);
  const [isUpdating, setIsUpdating] = useState(false);

  // Get available version from alert state
  const availableVersion = alertState?.alert?.availableVersion || '';

  // Get translations
  const bannerContent = t('alerts.app_update_available', {
    returnObjects: true,
  }) as any;

  // Logout function - will handle both Firebase and Clerk signout via Redux flow
  const handleLogout = useMobileLogout();

  const onUpdate = async () => {
    setIsUpdating(true);
    try {
      // First logout the user
      await handleLogout();

      // Then open the app store
      await AppUpdate.openAppStore();
    } catch (error) {
      console.error('Failed to open app store:', error);
      setIsUpdating(false);
    }
  };

  const onDismiss = () => {
    dispatch(ALERT.ResetAlert());
  };

  return (
    <div id="banner-app-update">
      {isUpdating && (
        <div className={styles.Alert__Loading__Overlay}>
          <div className={styles.Alert__Loading__Pulse}></div>
        </div>
      )}

      <div className={`${styles.Alert__Title} pb-1`}>
        {bannerContent?.title || 'Update Available'}
      </div>

      <div className={`paragraph ${styles.Alert__Content}`}>
        {bannerContent?.description
          ? bannerContent.description.replace('{{version}}', availableVersion)
          : `Version ${availableVersion} is available. We recommend updating for the latest features and improvements.`}
      </div>

      <div className={`${styles.Alert__Footer__Actions__Mobile} mt-2`}>
        <button
          className={`${styles.Alert__Footer__Actions__Mobile__Button} ${styles.Alert__Footer__Actions__Mobile__Button__Dismiss}`}
          onClick={onDismiss}
          disabled={isUpdating}
        >
          {bannerContent?.notNow || 'Not now'}
        </button>

        <button
          className={`${styles.Alert__Footer__Actions__Mobile__Button} ${styles.Alert__Footer__Actions__Mobile__Button__Confirm}`}
          onClick={onUpdate}
          disabled={isUpdating}
        >
          {bannerContent?.update || 'Update'}
        </button>
      </div>
    </div>
  );
}

export { AlertAppUpdate };
